/*
 * The addresses of a release note, spelled in exactly one place (S42, 2026-10-12).
 *
 * The same rule articleUrls.ts exists for, applied to the second kind of
 * content: a URL shape written out as a template literal at each call site is
 * invisible to the type checker, so a missed caller fails in a reader's browser
 * as a 404 instead of failing the build. Every release-note address is therefore
 * a function with required parameters, and each prefix below is written here and
 * nowhere else - the header links of both shells, the router, the admin sidebar,
 * the impact report, the ingest review link, the markdown image src and the web
 * client's API calls all come through this file.
 *
 * A release note's address is the PAIR (product slug, version): there is one
 * note per product and version (UQ_ReleaseNote_ProductId_Version) and no slug of
 * its own - the owner's answer, 2026-10-12:
 *
 *     /releasenotes                                                     the list
 *     /releasenotes?products=1,2                                        the list, filtered
 *     /releasenotes/<productSlug>/<version>                             one note
 *     /api/public/release-notes                                         the list's JSON
 *     /api/public/release-notes/<productSlug>/<version>                 one note's JSON
 *     /api/public/release-notes/<productSlug>/<version>/images/<file>   the bytes
 *     /admin/releasenotes, /admin/releasenotes/new, /admin/releasenotes/<id>
 *
 * Two spellings on purpose: the SITE pages use the owner's prefix, `releasenotes`
 * in one word, and the API uses kebab-case like every other API path. The public
 * image endpoint is addressed by the note's address rather than its id, as the
 * article image endpoint is, so an anonymous caller is never handed an id space
 * to walk; the admin pages are addressed by id, because the address is one of the
 * things the editor changes.
 *
 * The `/api` prefix is part of the API paths, as in articleUrls.ts: these are
 * WHOLE paths, the ones an <img src>, a router and a test asserting a URL use.
 * The web client's request helper takes the API-relative form and must be handed
 * these through its own conversion, not raw.
 *
 * Every segment is percent-encoded with encodeURIComponent. A stored product
 * slug and a stored version come through untouched - the slug's characters and
 * the version's digits, dots and v are all unreserved, and the dots in
 * `v1.2.3.4` are NOT encoded, which is correct: a dot is legal in a path
 * segment. So the encoding only matters for what is NOT a stored value - a
 * hand-typed route parameter, a file name with a space - which must not be able
 * to add a segment or a query. (A segment that is only `.` or `..` is the one
 * thing encoding cannot neutralise, since URL parsers read `%2E` as a dot; no
 * stored slug or version can be one.)
 */
/** The public list page. The one place the owner's `/releasenotes` prefix is written. */
export const RELEASE_NOTES_PATH = '/releasenotes';
/** The admin list page. The one place `/admin/releasenotes` is written. */
export const ADMIN_RELEASE_NOTES_PATH = '/admin/releasenotes';
/** The public API prefix. The one place `/api/public/release-notes` is written. */
const PUBLIC_RELEASE_NOTE_API_PREFIX = '/api/public/release-notes';
/**
 * The public list, optionally filtered to some products - the same
 * `?products=<ids>` the browse page's filter travels as, so the product box reads
 * it back the same way. Ids that are not positive whole numbers are dropped and
 * repeats are kept once, in first-seen order; an empty or absent list is the
 * unfiltered page, without a dangling `?products=`.
 */
export function releaseNotesHref(productIds) {
    const ids = [...new Set((productIds ?? []).filter((id) => Number.isInteger(id) && id > 0))];
    return ids.length > 0 ? `${RELEASE_NOTES_PATH}?products=${ids.join(',')}` : RELEASE_NOTES_PATH;
}
/** The page a reader visits for one release note, e.g. `/releasenotes/accoplanning/v1.0.87.3`. */
export function releaseNoteHref(productSlug, version) {
    return `${RELEASE_NOTES_PATH}/${segment(productSlug)}/${segment(version)}`;
}
/** The admin editor for one release note, addressed by its id. */
export function adminReleaseNoteHref(releaseNoteId) {
    return `${ADMIN_RELEASE_NOTES_PATH}/${segment(releaseNoteId)}`;
}
/** The admin page that creates a release note. */
export function adminNewReleaseNoteHref() {
    return `${ADMIN_RELEASE_NOTES_PATH}/new`;
}
/** The JSON endpoint for the public list; its query string is the caller's to add. */
export function publicReleaseNotesPath() {
    return PUBLIC_RELEASE_NOTE_API_PREFIX;
}
/** The JSON endpoint for one published release note. */
export function publicReleaseNotePath(productSlug, version) {
    return `${PUBLIC_RELEASE_NOTE_API_PREFIX}/${segment(productSlug)}/${segment(version)}`;
}
/**
 * The endpoint serving one image attached to a published release note. A string
 * rather than a fetch, because it ends up in an <img src>: the browser gets the
 * ETag, the conditional request and the disk cache for free.
 */
export function publicReleaseNoteImagePath(productSlug, version, fileName) {
    return `${publicReleaseNotePath(productSlug, version)}/images/${segment(fileName)}`;
}
function segment(value) {
    return encodeURIComponent(value);
}
//# sourceMappingURL=releaseNoteUrls.js.map